import React, { useState, useEffect, useMemo } from 'react';
import {
  FiPlus,
  FiSearch,
  FiFilter,
  FiCalendar,
  FiEdit2,
  FiTrash2,
  FiRepeat,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiUser
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import {
  PageComponent,
  Card,
  Table,
  Button,
  Input,
  Select,
  Modal,
  TableActions,
  Skeletons,
  MultiSelect
} from '../../components';
import HolidayModal from './HolidayModal';
import '../CompanyManager/style.css'; 

export default function HolidayManager() {
  const [loading, setLoading] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState(['all']);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [activeTab, setActiveTab] = useState('ativos'); // ativos, expirados, inativos
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  useEffect(() => {
    loadCompanies();
    loadHolidays();
  }, []);

  async function loadCompanies() {
    try {
      const data = await Api.getCompanies();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar empresas.');
    }
  }

  async function loadHolidays() {
    setLoading(true);
    try {
      // Backend expects a single companyId or 'all'.
      // If we want to filter by multiple companies, we should fetch 'all' and filter in frontend
      // OR update backend to accept multiple IDs.
      // Given the current backend implementation:
      // router.get('/holidays/company/:companyId', ...)
      // It takes one ID.
      
      // Strategy: Fetch 'all' and filter in frontend, OR iterate calls (bad), OR change backend.
      // The user wants "filtrar mais de uma empresa".
      // Let's change backend to support comma separated IDs or 'all'.
      // But first, let's just fetch 'all' and filter here for immediate response,
      // as fetching all is usually efficient enough if not paginated server-side.
      
      // Wait, listByCompany in backend handles 'all' by fetching all user companies.
      // So if we pass 'all', we get everything the user can see.
      // Then we can filter in memory.
      
      const response = await Api.get(`/holidays/company/all`);
      setHolidays(response.data || []);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar feriados.');
    } finally {
      setLoading(false);
    }
  }

  const filteredHolidays = useMemo(() => {
    let filtered = holidays;
    
    // Filter by Companies
    const isAllCompanies = selectedCompanyIds.includes('all');
    if (!isAllCompanies && selectedCompanyIds.length > 0) {
        // Need to check if holiday belongs to ANY of the selected companies.
        // Holiday has many Companies.
        // row.companies is an array of objects { id, ... }
        filtered = filtered.filter(h => {
            if (!h.companies) return false;
            return h.companies.some(c => selectedCompanyIds.includes(String(c.id)));
        });
    }

    // Filter by Search Term
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        filtered = filtered.filter(h => 
            h.description.toLowerCase().includes(lower) ||
            (h.date && h.date.includes(lower))
        );
    }
    
    // Filter by Tab
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    
    filtered = filtered.filter(h => {
        if (activeTab === 'inativos') {
            return !h.is_active; 
        }
        
        if (!h.is_active) return false;
        
        // For Ativos/Expirados
        if (h.repeat_yearly) {
            return activeTab === 'ativos';
        }
        
        if (h.date < today) {
            return activeTab === 'expirados';
        } else {
            return activeTab === 'ativos';
        }
    });
    
    return filtered;
  }, [holidays, searchTerm, activeTab, selectedCompanyIds]);

  const totalItems = filteredHolidays.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedHolidays = filteredHolidays.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
  );

  const counts = useMemo(() => {
      const today = new Date().toISOString().split('T')[0];
      return {
          ativos: holidays.filter(h => h.is_active && (h.repeat_yearly || h.date >= today)).length,
          expirados: holidays.filter(h => h.is_active && !h.repeat_yearly && h.date < today).length,
          inativos: holidays.filter(h => !h.is_active).length
      }
  }, [holidays]);

  function handleEdit(holiday) {
    setSelectedHoliday(holiday);
    setIsModalOpen(true);
  }

  function handleDelete(holiday) {
    setHolidayToDelete(holiday);
    setIsDeleteModalOpen(true);
  }

  async function confirmDelete() {
    if (!holidayToDelete) return;
    
    setIsDeleting(true);
    try {
      await Api.delete(`/holidays/${holidayToDelete.id}`);
      toast.success('Feriado removido com sucesso!');
      setHolidays(prev => prev.filter(h => h.id !== holidayToDelete.id));
      setIsDeleteModalOpen(false);
      setHolidayToDelete(null);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao remover feriado.');
    } finally {
        setIsDeleting(false);
    }
  }

  const columns = [
    { 
        id: 'id',
        header: 'ID', 
        width: '60px',
        cellClassName: 'manager-table-cell',
        render: (row) => <span className="manager-table-subtitle">#{row.id}</span>
    },
    { 
        id: 'date',
        header: 'Data', 
        width: '0.8fr',
        cellClassName: 'manager-table-cell',
        render: (row) => {
            if (!row.date) return '-';
            const parts = row.date.split('-');
            const dateStr = `${parts[2]}/${parts[1]}/${parts[0]}`; 
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500, color: 'var(--text-primary)' }}>
                    <FiCalendar size={14} color="var(--primary-color)" />
                    {dateStr.slice(0, 5)} 
                </div>
            );
        }
    },
    { 
        id: 'description',
        header: 'Descrição', 
        width: '2.5fr',
        cellClassName: 'manager-table-cell-primary',
        render: (row) => (
            <span className="manager-table-title">{row.description}</span>
        )
    },
    { 
        id: 'recorrente',
        header: 'Recorrente', 
        width: '1fr',
        cellClassName: 'manager-table-cell',
        render: (row) => (
            <div className="manager-tags">
                {row.repeat_yearly ? (
                    <span className="manager-tag manager-tag-success" style={{ gap: 4 }}>
                        <FiCheckCircle size={10} /> Sim
                    </span>
                ) : (
                    <span className="manager-table-subtitle">Não</span>
                )}
            </div>
        )
    },
    { 
        id: 'type',
        header: 'Tipo', 
        width: '1.2fr',
        cellClassName: 'manager-table-cell',
        render: (row) => (
            <span className="manager-table-subtitle" style={{textTransform: 'uppercase', fontSize: '0.75rem', fontWeight: 600}}>
                {row.type === 'PONTO_FACULTATIVO' ? 'Ponto Facultativo' : 'Feriado'}
            </span>
        )
    },
    { 
        id: 'abono',
        header: 'Abono', 
        width: '1.2fr',
        cellClassName: 'manager-table-cell',
        render: (row) => (
             <div className="manager-tags">
                <span className="manager-tag manager-tag-info">
                    {row.abono.replace(/_/g, ' ')}
                </span>
             </div>
        )
    },
    { 
        id: 'postos',
        header: 'Postos', 
        width: '1fr',
        cellClassName: 'manager-table-cell',
        render: (row) => {
            if (row.apply_to_all_stations) {
                return (
                    <div className="manager-tags">
                        <span className="manager-tag manager-tag-neutral" style={{ fontWeight: 'bold' }}>TODOS</span>
                    </div>
                );
            }
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1 }}>
                    <span style={{ fontWeight: 'bold', fontSize: '0.9rem', color: 'var(--text-primary)' }}>{row.serviceStations?.length || 0}</span>
                    <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Postos</span>
                </div>
            );
        }
    },
    { 
        id: 'responsavel',
        header: 'Responsável',
        width: '1.5fr',
        cellClassName: 'manager-table-cell',
        render: (row) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                 <div className="manager-table-avatar" style={{ width: 24, height: 24, fontSize: 10 }}>
                    <span style={{ fontWeight: 'bold' }}>
                        {row.creator ? row.creator.name.substring(0, 2).toUpperCase() : 'AD'}
                    </span>
                 </div>
                 <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span className="manager-table-title" style={{ fontSize: '0.75rem' }}>
                        {row.creator ? row.creator.name.toUpperCase() : 'ADMINISTRADOR'}
                    </span>
                    <span className="manager-table-subtitle" style={{ fontSize: '0.65rem' }}>{new Date(row.updatedAt || row.createdAt).toLocaleDateString()}</span>
                 </div>
            </div>
        )
    },
    { 
        id: 'actions',
        header: 'Ações', 
        width: '0.6fr',
        cellClassName: 'manager-table-cell manager-table-col-actions',
        render: (row) => (
          <TableActions>
            <button onClick={(e) => { e.stopPropagation(); handleEdit(row); }}>
                <FiEdit2 size={16} /> Editar
            </button>
            <button className="action-danger" onClick={(e) => { e.stopPropagation(); handleDelete(row); }}>
                <FiTrash2 size={16} /> Excluir
            </button>
          </TableActions>
        )
    }
  ];
  
  function HolidayManagerSkeleton() {
    const { CompanyManagerSkeleton } = Skeletons;
    return <CompanyManagerSkeleton />;
  }

  return (
    <PageComponent authenticatedPage={true}>
      <div className="container">
      {loading ? <HolidayManagerSkeleton /> : (
        <>
            <div className="manager-header">
              <div className="manager-header-title">
                <div className="manager-header-icon">
                  <FiCalendar size={22} />
                </div>
                <div>
                  <h1>Feriados</h1>
                  <p>Gerencie os feriados e pontos facultativos</p>
                </div>
              </div>
              <Button onClick={() => { setSelectedHoliday(null); setIsModalOpen(true); }} className="manager-header-button">
                <FiPlus size={18} />
                Novo Feriado
              </Button>
            </div>

            <Card className="manager-filters-card">
                <div className="manager-filters-header">
                <div className="manager-filters-title">
                    <FiFilter size={16} />
                    <span>Filtros</span>
                </div>
                {filteredHolidays.length > 0 && (
                    <span className="manager-filters-count">
                    {filteredHolidays.length} registros
                    </span>
                )}
                </div>
                
                <div className="manager-filters-grid">
                    <div className="manager-filter-group">
                        <label className="manager-filter-label">Empresa</label>
                        <MultiSelect
                            id="company-filter"
                            value={selectedCompanyIds}
                            onChange={(newValues) => {
                                const allValue = 'all';
                                const currentSelected = selectedCompanyIds;
                                const isAllSelected = newValues.includes(allValue);
                                const wasAllSelected = currentSelected.includes(allValue);
                                
                                let nextValues;
                                const allIds = companies.map(c => String(c.id));
                                
                                if (isAllSelected && !wasAllSelected) {
                                    nextValues = [allValue, ...allIds];
                                } else if (!isAllSelected && wasAllSelected) {
                                    nextValues = [];
                                } else if (wasAllSelected && newValues.length < currentSelected.length) {
                                    const remaining = newValues.filter(v => v !== allValue);
                                    nextValues = remaining;
                                } else {
                                    const specificIds = newValues.filter(v => v !== allValue);
                                    if (specificIds.length === allIds.length && allIds.length > 0) {
                                        nextValues = [allValue, ...specificIds];
                                    } else {
                                        nextValues = specificIds;
                                    }
                                }
                                setSelectedCompanyIds(nextValues);
                            }}
                            options={[
                                { value: 'all', label: 'TODAS' },
                                ...companies.map(c => ({ value: String(c.id), label: c.trade_name || c.corporate_name }))
                            ]}
                            placeholder="Selecione..."
                        />
                    </div>
                    
                    <div className="manager-filter-group">
                        <label className="manager-filter-label">Buscar</label>
                        <div className="manager-filter-search">
                            <Input
                                placeholder="Buscar por descrição..."
                                icon={FiSearch}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div style={{ marginTop: 24, display: 'flex', gap: 12, alignItems: 'center' }}>
                    <div className="manager-filter-chips">
                        <button 
                            className={`manager-chip ${activeTab === 'ativos' ? 'manager-chip-active' : ''}`}
                            onClick={() => setActiveTab('ativos')}
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                            <FiCheckCircle size={14} />
                            Ativos 
                            <span style={{ 
                                background: activeTab === 'ativos' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)', 
                                padding: '0 6px', 
                                borderRadius: 10, 
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                            }}>
                                {counts.ativos}
                            </span>
                        </button>
                        <button 
                            className={`manager-chip ${activeTab === 'expirados' ? 'manager-chip-active' : ''}`}
                            onClick={() => setActiveTab('expirados')}
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                            <FiClock size={14} />
                            Expirados 
                            <span style={{ 
                                background: activeTab === 'expirados' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)', 
                                padding: '0 6px', 
                                borderRadius: 10, 
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                            }}>
                                {counts.expirados}
                            </span>
                        </button>
                        <button 
                            className={`manager-chip ${activeTab === 'inativos' ? 'manager-chip-active' : ''}`}
                            onClick={() => setActiveTab('inativos')}
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                            <FiXCircle size={14} />
                            Inativos 
                            <span style={{ 
                                background: activeTab === 'inativos' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.05)', 
                                padding: '0 6px', 
                                borderRadius: 10, 
                                fontSize: '0.75rem',
                                fontWeight: 'bold'
                            }}>
                                {counts.inativos}
                            </span>
                        </button>
                    </div>
                </div>
            </Card>

            <Table
                columns={columns}
                items={paginatedHolidays}
                loading={loading}
                emptyMessage="Nenhum feriado encontrado."
                totalItems={totalItems}
                page={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
            />
        </>
      )}
      </div>

      <HolidayModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialData={selectedHoliday}
        onSuccess={loadHolidays}
        companies={companies}
      />
      
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Confirmar exclusão"
        closeOnOverlayClick={!isDeleting}
      >
        <div className="delete-modal-content">
          <p>
            Tem certeza que deseja excluir o feriado{' '}
            {holidayToDelete && (
              <span className="delete-modal-company-name">
                {holidayToDelete.description}
              </span>
            )}
            ?
          </p>
          <p>
            Essa ação não poderá ser desfeita.
          </p>
        </div>
        <div className="delete-modal-actions">
          <Button
            type="button"
            className="cancel-button"
            onClick={() => setIsDeleteModalOpen(false)}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={confirmDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </div>
      </Modal>

    </PageComponent>
  );
}
